import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack, Popover, TextField, CircularProgress } from "@mui/material";
import ErrorMsg from "../ErrorMsg";
import BtnPrimary from "../PrimaryButton";
import BtnSecondary from "../SecondaryButton";
import { tokens } from "../../theme";

/**
 * Correcting one day's punches from the register.
 *
 * This does NOT write the day record directly. It files a regularization —
 * the same record an employee's own request creates — marked as attested by
 * the person doing it. An admin edit that changes someone's paid hours with no
 * author, no reason and no timestamp is the thing this exists to prevent, and
 * a second write path around the request pipeline would reintroduce it.
 *
 * The remark is required for the same reason: a correction nobody explained is
 * indistinguishable from a mistake when someone reads the register back months
 * later during a payroll dispute.
 */

// minutes since midnight <-> the "HH:MM" a native time input speaks
const toTimeInput = (mins) =>
  mins == null || Number.isNaN(mins)
    ? ""
    : `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

const fromTimeInput = (value) => {
  if (!value) return null;
  const [h, m] = value.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

const fieldSx = {
  "& .MuiInputBase-root": { fontSize: "0.875rem", borderRadius: "8px", background: tokens.card },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: tokens.border },
};

const Label = ({ children }) => (
  <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.25rem" }}>
    {children}
  </Box>
);

const PunchCorrection = ({ anchorEl, day, dateLabel, onClose, onSubmit, isOwnRegister = false }) => {
  const [inTime, setInTime] = useState("");
  const [outTime, setOutTime] = useState("");
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Field-level, marked when you leave the control. `error` stays for the two
  // things that are not a field: nothing changed to save, and a failed write.
  const [fieldErrors, setFieldErrors] = useState({});
  const markField = (field, message) =>
    setFieldErrors((f) => ({ ...f, [field]: message || "" }));

  // Seed the fields from the day being edited — but keyed on *which* day, not on
  // the object's identity. The register rebuilds its day objects on every
  // refetch, and resetting on identity would wipe half-typed input the moment
  // anything upstream re-rendered.
  const dayRef = useRef(day);
  dayRef.current = day;
  const dayKey = day?.dayString || null;

  useEffect(() => {
    const d = dayRef.current;
    if (!d) return;
    setInTime(toTimeInput(d.arrivalTime));
    setOutTime(toTimeInput(d.departureTime));
    setRemark("");
    setError("");
    setSaving(false);
  }, [dayKey]);

  const nextIn = fromTimeInput(inTime);
  const nextOut = fromTimeInput(outTime);

  const changed = useMemo(() => {
    if (!day) return {};
    return {
      arrival: nextIn !== (day.arrivalTime ?? null),
      departure: nextOut !== (day.departureTime ?? null),
    };
  }, [day, nextIn, nextOut]);

  const somethingChanged = changed.arrival || changed.departure;

  // One definition each, used by both the blur and the save — two copies drift,
  // and the copy that drifts is always the one the button uses.
  const checkOutOrder = () =>
    nextIn != null && nextOut != null && nextOut <= nextIn ? "Check-out must be after check-in." : "";
  const checkRemark = () =>
    !remark.trim() ? "A remark is required — it's the record of why this changed." : "";

  const save = async () => {
    setError("");
    setFieldErrors({});
    if (!somethingChanged) {
      setError("Change a time before saving.");
      return;
    }
    // clearing a punch isn't a correction the API models — it only ever sets a
    // time — so say that rather than sending a request that will be ignored
    if ((changed.arrival && nextIn == null) || (changed.departure && nextOut == null)) {
      setError("A punch can be corrected but not removed.");
      return;
    }
    const order = checkOutOrder();
    const needsRemark = checkRemark();
    if (order || needsRemark) {
      markField("outTime", order);
      markField("remark", needsRemark);
      return;
    }

    setSaving(true);
    // send only what actually moved, so an untouched punch isn't restated as a
    // correction of itself in the audit trail
    const payload = { reason: remark.trim() };
    if (changed.arrival) payload.updatedClockInTime = nextIn;
    if (changed.departure) payload.updatedClockOutTime = nextOut;

    const res = await onSubmit(payload);
    setSaving(false);
    if (res?.success) {
      onClose();
    } else {
      setError(res?.message || "Couldn't save the correction.");
    }
  };

  return (
    <Popover
      open={Boolean(anchorEl && day)}
      anchorEl={anchorEl}
      onClose={saving ? undefined : onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      slotProps={{
        paper: {
          sx: {
            mt: 0.5,
            width: "20rem",
            p: "1rem",
            borderRadius: "12px",
            border: `1px solid ${tokens.border}`,
            boxShadow: "0 16px 40px -20px rgba(42,29,34,0.22)",
          },
        },
      }}
    >
      <Box sx={{ fontSize: "0.875rem", fontWeight: 600, color: tokens.text }}>
        {isOwnRegister ? "Request a correction" : "Correct punches"}
      </Box>
      <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "1rem" }}>
        {dateLabel}
      </Box>

      <Stack direction="row" spacing={1.25} sx={{ mb: "1rem" }}>
        <Box sx={{ flex: 1 }}>
          <Label>Check in</Label>
          <TextField
            type="time" size="small" fullWidth value={inTime}
            onChange={(e) => { setInTime(e.target.value); markField("outTime", ""); }}
            onBlur={() => markField("outTime", checkOutOrder())}
            sx={fieldSx}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Label>Check out</Label>
          <TextField
            type="time" size="small" fullWidth value={outTime}
            onChange={(e) => { setOutTime(e.target.value); markField("outTime", ""); }}
            onBlur={() => markField("outTime", checkOutOrder())}
            sx={fieldSx}
          />
        </Box>
      </Stack>

      <ErrorMsg>{fieldErrors.outTime}</ErrorMsg>

      <Box sx={{ mb: "1rem" }}>
        <Label>Remark (required)</Label>
        <TextField
          size="small" fullWidth multiline minRows={2}
          placeholder="e.g. reader offline on the floor"
          value={remark}
          onChange={(e) => { setRemark(e.target.value); markField("remark", ""); }}
          onBlur={() => markField("remark", checkRemark())}
          error={Boolean(fieldErrors.remark)}
          sx={fieldSx}
        />
        <ErrorMsg>{fieldErrors.remark}</ErrorMsg>
      </Box>

      {error && (
        <Box sx={{ fontSize: "0.8125rem", color: tokens.danger, mb: "0.75rem" }}>{error}</Box>
      )}

      {/* An attestation is the decision of someone empowered to make it and
          applies at once; your own is a request and may wait for approval.
          Promising immediate application in both cases would be a lie in one. */}
      <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "1rem", lineHeight: 1.5 }}>
        {isOwnRegister
          ? "Filed as your own correction request, with this remark. It may need approval before the record changes."
          : "Saved as an attendance correction in your name, with this remark, and applied to the day record immediately."}
      </Box>

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <BtnSecondary onClick={onClose} disabled={saving} sx={{ borderRadius: "8px" }}>
          Cancel
        </BtnSecondary>
        <BtnPrimary onClick={save} disabled={saving} sx={{ borderRadius: "8px" }}>
          {saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Save correction"}
        </BtnPrimary>
      </Stack>
    </Popover>
  );
};

export default PunchCorrection;
